import { loadScript } from '../../utils';

const turnstileUrl = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Only currently used options are defined.
 * See https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/#configurations for more possible options.
 *
 * There's also @types/cloudflare-turnstile package we could probably use instead.
 */
interface TurnstileRenderOptions {
    /**
     * Cloudflare Turnstile sitekey
     */
    sitekey: string;
    /**
     * Callback that is invoked upon success of the challenge.
     * The callback is passed a token that can be validated.
     */
    callback?: (token: string) => void;
    /**
     * Callback that is invoked when the challenge is unsuccessful.
     */
    'error-callback'?: (error?: string | Error) => void;
    /**
     * A customer value that can be used to differentiate widgets under the same sitekey in analytics and which is returned upon validation.
     */
    action?: string;
    /**
     * Controls when is the turnstile widget visible.
     * "always" is always, "execute" only after the challenge begins, "interaction-only" is invisible, unless interaction is required.
     */
    appearance?: 'always' | 'execute' | 'interaction-only';
    /**
     * Indicates whether to create a hidden form input with the token as value or not.
     */
    'response-field'?: boolean;
}

declare global {
    interface Window {
        turnstile: {
            /**
             * Invokes a Turnstile widget and returns the ID of the newly created widget.
             * - @param container The HTML element to render the Turnstile widget into. Specify either the ID of HTML element (string), or the DOM element itself.
             * - @return the ID of the newly created widget, or undefined if invocation is unsuccessful.
             */
            render: (container: string | HTMLElement, options?: TurnstileRenderOptions) => string | undefined;
        };
    }
}

export interface CaptchaService {
    initialize: () => Promise<void>;
    isInitialized: () => boolean;
    createChallenge: (container: string | HTMLElement, options: ChallengeOptions) => string | undefined | null;
}

export interface ChallengeOptions {
    /**
     * A successful challenge callback.
     * - @param token the captcha token to be validated
     */
    onSuccess?: (token: string) => void;

    /**
     * An unsuccessful challenge callback.
     */
    onError?: (error?: string | Error) => void;

    /**
     * A user-defined string used to differentiate different uses of the same sitekey.
     */
    challengeLabel?: string;
}

export const createCaptchaService = ({ sitekey }: { sitekey: string }): CaptchaService => {
    const getInstance = () => window.turnstile;
    let initialized = false;

    return {
        initialize: async () => {
            if (sitekey) {
                await loadScript(turnstileUrl);
            }
            initialized = true;
        },

        isInitialized: () => initialized,

        createChallenge: (container, { onSuccess, onError, challengeLabel }) => {
            const instance = getInstance();
            if (!instance) {
                return;
            }

            return instance.render(container, {
                sitekey,
                callback: onSuccess,
                'error-callback': onError,
                action: challengeLabel,

                /**
                 * These values are currently hardcoded. If necessary to change or if we need to use more Turnstile options,
                 * extend the {@link ChallengeOptions} interface and turn these into parameters.
                 */
                appearance: 'interaction-only',
                'response-field': false,
            });
        },
    };
};
